import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Grid, Switch, FormControlLabel, Paper, Divider, Chip, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { PermissionDefinitionsByCategory, TierPermissions } from '../../../services/PermissionService'

interface TierPermissionsEditorProps {
  tierId: string
  definitions: PermissionDefinitionsByCategory
  initialPermissions: TierPermissions
  onChange: (perms: TierPermissions) => void
}

const TierPermissionsEditor: React.FC<TierPermissionsEditorProps> = ({
  tierId,
  definitions,
  initialPermissions,
  onChange,
}) => {
  const theme = useTheme()
  const [permissions, setPermissions] = useState<TierPermissions>(initialPermissions || {})

  useEffect(() => {
    setPermissions(initialPermissions || {})
  }, [tierId, initialPermissions])

  const categories = useMemo(() => Object.keys(definitions), [definitions])

  const handleToggle = (key: string, value: boolean) => {
    const updated = { ...permissions, [key]: value }
    setPermissions(updated)
    onChange(updated)
  }

  const cardSx = {
    p: 2,
    mb: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    boxShadow: 'none',
    transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: theme.transitions.duration.shorter }),
    '&:last-of-type': { mb: 0 },
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      boxShadow: theme.shadows[theme.palette.mode === 'dark' ? 4 : 1],
    },
  } as const

  return (
    <Box sx={{ mt: 0 }}>
      {categories.map((category) => (
        <Paper key={category} elevation={0} sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '1rem',
                lineHeight: 1.3,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              }}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Typography>
            <Chip
              size="small"
              label={`${definitions[category].length} items`}
              variant="outlined"
              color="primary"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                borderColor: alpha(theme.palette.primary.main, 0.45),
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              }}
            />
          </Box>
          <Divider sx={{ mb: 2, borderColor: 'divider' }} />
          <Grid container spacing={2}>
            {definitions[category].map((def) => {
              const enabled = permissions[def.key] ?? !!def.defaultEnabled
              return (
                <Grid item xs={12} md={6} key={def.key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={(e) => handleToggle(def.key, e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: 'primary.main',
                            backgroundImage: 'none',
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            color: 'text.primary',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                            lineHeight: 1.5,
                          }}
                        >
                          {def.label}
                        </Typography>
                        {def.description ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                              lineHeight: 1.4,
                              display: 'block',
                            }}
                          >
                            {def.description}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                  />
                </Grid>
              )
            })}
          </Grid>
        </Paper>
      ))}
    </Box>
  )
}

export default TierPermissionsEditor
